<template>
    <div
        v-if="kind"
        class="app-error"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="app-error-title"
        aria-describedby="app-error-description"
    >
        <div class="app-error__card">
            <!-- Крестика нет, когда закрывать некуда: приложение не загрузилось, и под
                 оверлеем один спиннер. -->
            <button v-if="closable" type="button" class="app-error__close" aria-label="Закрыть" @click="close">
                ×
            </button>

            <!-- Крупная цифра за вертикальной чертой — мотив прежней страницы ошибки; он
                 остался, чтобы экран узнавался. У сетевого сбоя и незагрузившегося приложения
                 кода нет, и тогда блок не рисуется вовсе: пустая черта выглядела бы обрывом. -->
            <div class="app-error__body">
                <div v-if="presentation.code" class="app-error__code">{{ presentation.code }}</div>

                <div class="app-error__text">
                    <h1 id="app-error-title" class="app-error__title">{{ presentation.title }}</h1>
                    <p id="app-error-description" class="app-error__description">{{ presentation.description }}</p>

                    <VButton ref="action" type="button" class="button_green app-error__action" @click="runAction">
                        {{ presentation.actionLabel }}
                    </VButton>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
// Components
import VButton from '../VButton/VButton.vue';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { appErrorState, clearAppError, describeAppError } from '@/js/appError';
import { resetAuthRedirects } from '@/js/authRedirectGuard';

// Экран фатальной ошибки — оверлеем поверх текущей страницы, а не отдельным маршрутом.
// Разница принципиальная: адрес остаётся тем, что пользователь открывал, поэтому кнопка
// «Перезагрузить страницу» повторяет исходный запрос и обычно чинит ситуацию. Прежний
// редирект на /error/500 перезагружал сам себя, и выйти из него можно было только правкой
// адресной строки.
//
// Закрываемость зависит от того, что под оверлеем (appErrorState.closable): рабочий экран, на
// котором упал один фоновый запрос, запирать насмерть нельзя — крестик и Esc его отпускают.
// А несостоявшуюся загрузку приложения закрывать некуда: под ней только спиннер, и выход
// оттуда один — кнопка перезагрузки.
@Component({
    components: {
        VButton,
    },
})
class VAppErrorOverlay extends Vue {
    get kind() {
        return appErrorState.kind;
    }

    get presentation() {
        return describeAppError(this.kind);
    }

    get closable() {
        return appErrorState.closable;
    }

    @Watch('kind', { immediate: true })
    onKindChange(kind) {
        if (!kind) {
            return;
        }

        // Фокус на кнопку: с клавиатуры до единственного действия иначе не добраться, не
        // пройдя весь экран под оверлеем. nextTick — блок рендерится по v-if.
        this.$nextTick(() => {
            const action = this.$refs.action;

            if (action && action.$el && action.$el.focus) {
                action.$el.focus();
            }
        });
    }

    runAction() {
        // Свежий лимит редиректов даётся ручной попытке — и только ей. Сбрасывать
        // счётчик при показе экрана нельзя: пока экран висит, лимит обязан
        // действовать, иначе первый же следующий 401 снова уводит на SSO и цикл
        // «три редиректа → экран ошибки» повторяется без конца.
        resetAuthRedirects();

        if (this.presentation.action === 'home') {
            window.location.assign('/');
            return;
        }

        window.location.reload();
    }

    close() {
        if (!this.closable) {
            return;
        }

        // Закрытие — тоже осознанное действие пользователя: он возвращается на рабочий
        // экран под оверлеем, и автоматика получает право на новую серию попыток.
        resetAuthRedirects();
        clearAppError();
    }

    onKeydown(event) {
        if (event.key === 'Escape' && this.kind) {
            this.close();
        }
    }

    mounted() {
        // Слушатель на документе, а не на корне оверлея: фокус может остаться на элементе
        // под ним (оверлей появляется поверх уже отрисованного экрана), и keydown до нас
        // просто не всплыл бы.
        document.addEventListener('keydown', this.onKeydown);
    }

    beforeDestroy() {
        document.removeEventListener('keydown', this.onKeydown);
    }
}

export default VAppErrorOverlay;
</script>
<style lang="scss">
.app-error {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: $layer-app-error;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background-color: rgba(36, 36, 36, 0.55);

    &__card {
        position: relative;
        width: 100%;
        max-width: 620px;
        padding: 40px 45px;
        border-radius: 8px;
        background-color: var(--color-white);
        box-shadow: 0 4px 30px rgba(44, 44, 44, 0.25);
    }

    &__close {
        position: absolute;
        top: 10px;
        right: 14px;
        padding: 0 6px;
        border: none;
        background: none;
        color: #a0a0a0;
        font: 300 1.75rem var(--font-family);
        line-height: 1;
        cursor: pointer;

        &:hover {
            color: #636b6f;
        }
    }

    &__body {
        display: flex;
        align-items: center;
        gap: 25px;
    }

    // Мотив прежней страницы ошибки: тонкая крупная цифра, отделённая вертикальной чертой.
    &__code {
        flex-shrink: 0;
        padding-right: 25px;
        border-right: 2px solid #636b6faa;
        color: #636b6f;
        font: 300 3.5rem var(--font-family);
        line-height: 1;
    }

    &__text {
        min-width: 0;
    }

    &__title {
        margin: 0;
        color: var(--color-black);
        font: 400 1.35rem var(--font-family);
    }

    &__description {
        margin: 8px 0 22px;
        color: #767676;
        font: 300 1rem var(--font-family);
        line-height: 1.45;
    }

    // Узкий экран: цифра над текстом смотрелась бы обрубком рядом с длинным заголовком,
    // поэтому в колонку уходит весь блок, а вертикальная черта становится горизонтальной.
    @media (max-width: $media-sm) {
        &__body {
            flex-direction: column;
            align-items: flex-start;
            gap: 15px;
        }

        &__code {
            padding: 0 0 12px;
            border-right: none;
            border-bottom: 2px solid #636b6faa;
        }
    }
}
</style>
